import type { GameSceneHandle, SceneDeps } from "@slot/game-shell";
import type { PresentationTicker } from "@slot/presentation";
import { loadGameVisualAssets, type GameVisualAssets } from "../assets/GameVisualAssets";
import { riveRuntime } from "../rive/riveRuntime";
import { BACKGROUND_TUNING } from "../tuning/backgroundTuning";
import { GameBackground } from "./GameBackground";
import { COMPOSITION_ROOT, GAME_LOGO, GameScene } from "./GameScene";
import { GameModals } from "./GameModals";

/** This game's scene as the shell sees it, plus what its round presentation reads. */
export interface MergeCandyScene extends GameSceneHandle {
  game: GameScene;
  /** Under the canvas, over the shell's still (decision 69): the candy factory animated; null while it is kept still. */
  background: GameBackground | null;
  gameModals: GameModals;
  assets: GameVisualAssets;
  /** Engine time for the frame-driven loops the presentation draws outside a round (the Secret Bonus hand, the chef's cooldown). */
  presentationTicker: PresentationTicker;
}

/** The scene and the round's panels from the loaded assets. */
export function createScene(deps: SceneDeps): MergeCandyScene {
  const assets = loadGameVisualAssets({ payload: deps.payload, log: deps.log, secretBonusLabel: deps.services.localization.t("secret_bonus.symbol", "SECRET\nBONUS") });
  const game = new GameScene({ assets, viewport: deps.viewport, contentArea: deps.contentArea, ticker: deps.presentationTicker, log: deps.log });
  if (!assets.background) deps.log("[createScene] no background file: the still shows");
  // Dev mode's `?background=still` keeps the factory still (the e2e's: headless browsers draw the animation in software).
  const background =
    deps.backgroundRoot && assets.background && !deps.backgroundStillOnly
      ? new GameBackground({
          root: deps.backgroundRoot,
          url: assets.background,
          viewport: deps.viewport,
          ticker: deps.presentationTicker,
          runtime: riveRuntime,
          tuning: BACKGROUND_TUNING,
          log: deps.log,
        })
      : null;
  const gameModals = new GameModals({ assets, localization: deps.services.localization, runner: deps.services.chromeTaskRunner, viewport: deps.viewport });
  return {
    node: game,
    modalsNode: gameModals,
    compositionRoot: game.compositionRoot,
    compositionSize: { width: COMPOSITION_ROOT.width, height: COMPOSITION_ROOT.height },
    logo: GAME_LOGO,
    game,
    background,
    gameModals,
    assets,
    presentationTicker: deps.presentationTicker,
  };
}
